import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SubscriptionPlan } from '@lingugeek/schemas';
import type { SubscriptionState, SubscriptionActions } from '../types';
import { getApiService } from '../api/services';

interface SubscriptionStore extends SubscriptionState {
  actions: SubscriptionActions;
}

export const useSubscriptionStore = create<SubscriptionStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      subscription: null,
      isLoading: false,
      error: null,

      // Actions
      actions: {
        loadSubscription: async () => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'subscription/loadSubscription/start'
            );

            const apiService = getApiService();
            const subscription = await apiService.getSubscription();

            set(
              {
                subscription,
                isLoading: false,
                error: null,
              },
              false,
              'subscription/loadSubscription/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load subscription';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'subscription/loadSubscription/error'
            );
            throw error;
          }
        },

        upgradeSubscription: async (plan: SubscriptionPlan) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'subscription/upgradeSubscription/start'
            );

            const apiService = getApiService();
            const result = await apiService.upgradeSubscription(plan);

            // If we get a checkout URL, we need to redirect
            if ('url' in result) {
              try {
                const url = new URL(result.url);
                // Allow same origin or trusted checkout providers
                if (
                  url.origin === window.location.origin ||
                  url.hostname.includes('stripe.com') ||
                  url.hostname.includes('checkout.stripe.com')
                ) {
                  window.location.href = result.url;
                }
              } catch (error) {
                console.error('Invalid checkout URL:', result.url);
              }
              return;
            }

            // Otherwise, reload subscription data
            await get().actions.loadSubscription();

            set(
              {
                isLoading: false,
                error: null,
              },
              false,
              'subscription/upgradeSubscription/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to upgrade subscription';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'subscription/upgradeSubscription/error'
            );
            throw error;
          }
        },

        downgradeSubscription: async (plan: SubscriptionPlan) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'subscription/downgradeSubscription/start'
            );

            const apiService = getApiService();
            await apiService.downgradeSubscription(plan);

            // Reload subscription data
            await get().actions.loadSubscription();

            set(
              {
                isLoading: false,
                error: null,
              },
              false,
              'subscription/downgradeSubscription/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to downgrade subscription';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'subscription/downgradeSubscription/error'
            );
            throw error;
          }
        },

        cancelSubscription: async () => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'subscription/cancelSubscription/start'
            );

            const apiService = getApiService();
            await apiService.cancelSubscription();

            // Reload subscription data
            await get().actions.loadSubscription();

            set(
              {
                isLoading: false,
                error: null,
              },
              false,
              'subscription/cancelSubscription/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to cancel subscription';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'subscription/cancelSubscription/error'
            );
            throw error;
          }
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'subscription/setLoading');
        },

        setError: (error: string | null) => {
          set({ error }, false, 'subscription/setError');
        },

        setSubscription: (subscription: SubscriptionState['subscription']) => {
          set({ subscription }, false, 'subscription/setSubscription');
        },

        updateSubscription: (
          subscription: SubscriptionState['subscription']
        ) => {
          set({ subscription }, false, 'subscription/updateSubscription');
        },
      },
    }),
    {
      name: 'subscription-store',
    }
  )
);
